'use client';

import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useSearchParams, useRouter } from 'next/navigation';
import Image from 'next/image';
import { MOBILE, useDeviceType } from '@/ueshook/useDeviceType';
import usePasteImage from '@/ueshook/usePasteImage';
import { RPostHog } from '@/posthog';
import { posthogUTM } from '@/posthog';

const { utm } = posthogUTM();

// 类型定义优化
export interface IntroUploadBaseProps {
  maxImageCount: number;
  uploading: boolean;
  multipleUpload: boolean;
  onPaste: (file: File) => void;
  onFileSelect: (files: FileList) => void;
  onFileDrop: (files: FileList) => void;
}

// 提取上传中组件
const UploadingIndicator = () => (
  <div className='flex h-56 flex-col items-center justify-center'>
    <div className='h-16 w-16 animate-spin rounded-full border-4 border-t-4 border-gray-300 border-t-blue-500' />
    <span>Uploading...</span>
  </div>
);

// 提取图片预览组件
const ImagePreview = ({
  imageUrl,
  onNewUpload,
  onUseImage,
}: {
  imageUrl: string;
  onNewUpload: () => void;
  onUseImage: () => void;
}) => (
  <div className='flex flex-col'>
    <div className='max-h-150 flex items-center justify-center'>
      <Image
        src={imageUrl}
        alt='originalImg'
        width={520}
        height={600}
        style={{ objectFit: 'cover' }}
        className='rounded-xl'
      />
    </div>

    <div className='flex w-full justify-between px-5 pt-6'>
      <button
        onClick={() => {
          RPostHog('inner_link_new_upload', { ...utm });
          onNewUpload();
        }}
        className='flex h-12 w-[220px] items-center justify-center rounded-md border border-[#E4E4E7] bg-[#ffffff] px-8 py-2 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] hover:bg-gray-100'
      >
        New Upload
      </button>

      <button
        onClick={() => {
          RPostHog('inner_link_use_this_image', { ...utm });
          onUseImage();
        }}
        className='flex h-12 w-[220px] items-center justify-center rounded-md bg-[#252933] px-8 py-2 text-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10),_0px_1px_2px_0px_rgba(0,0,0,0.06)] hover:bg-slate-700'
      >
        Use This Image
      </button>
    </div>
  </div>
);

// 提取上传按钮组件
const UploadButton = ({
  MAX_IMAGES,
  onClick,
  multipleUpload,
  deviceType,
}: {
  MAX_IMAGES: number;
  onClick: () => void;
  multipleUpload: boolean;
  deviceType: string;
}) => (
  <>
    <button
      onClick={onClick}
      className='h-fit w-fit rounded-xl bg-gray-800 px-9 py-[14px] text-[20px] font-semibold text-stone-50 hover:bg-gray-700 md:text-[28px]'
    >
      Upload image
    </button>
    <p className='text-nowrap text-[20px] text-gray-500'>
      {deviceType === MOBILE
        ? `Click up to ${MAX_IMAGES > 1 ? `${MAX_IMAGES} images` : 'an image'}.`
        : `Drop, click, or paste up to ${MAX_IMAGES > 1 ? `${MAX_IMAGES} images` : 'an image'}.`}
    </p>
  </>
);

// 提取条款组件
const TermsAndPolicy = ({ router }: { router: any }) => (
  <div className='px-4 pt-4'>
    <p className='text-center text-xs leading-normal text-zinc-700 md:text-base'>
      By uploading images, you agree to our{' '}
      <span
        className='cursor-pointer underline underline-offset-4'
        onClick={() => router.push('/terms')}
      >
        Terms of Service
      </span>{' '}
      and review our{' '}
      <span
        className='cursor-pointer underline underline-offset-4'
        onClick={() => router.push('/privacy')}
      >
        Privacy Policy
      </span>
      .
    </p>
  </div>
);

export const IntroUpload = ({
  maxImageCount,
  multipleUpload,
  uploading,
  onPaste,
  onFileSelect,
  onFileDrop,
}: IntroUploadBaseProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const router = useRouter();
  const { handlePaste } = usePasteImage();
  const deviceType = useDeviceType();

  const searchParams = useSearchParams();
  const originalImgUrl = searchParams.get('originalImgUrl');

  // 处理文件选择点击事件
  const handleFileInputClick = useCallback(() => {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }, []);

  // 处理粘贴事件
  const onPasteHandler = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      handlePaste(
        event,
        (file) => onPaste(file),
        (error) => toast.error(`Failed to paste image: ${error}`)
      );
    },
    [handlePaste, onPaste]
  );

  // 处理文件选择事件
  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // console.log(
      //   `选择完成${event.target.files?.length}张图像:`,
      //   timestampToFormattedTime()
      // );

      const files = event.target.files;
      if (!files || files.length === 0) return;

      onFileSelect(files);
      // 重置输入框的值，允许重复选择相同的文件
      event.target.value = '';
    },
    [onFileSelect]
  );

  // 处理拖放事件
  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);

      const files = event.dataTransfer.files;
      if (!files || files.length === 0) return;

      onFileDrop(files);
    },
    [onFileDrop]
  );

  // 处理拖拽悬停事件
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  // 处理拖拽离开事件
  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 处理使用当前图片事件
  const handleUseCurrentImage = useCallback(async () => {
    if (!originalImgUrl) return;

    try {
      const response = await fetch(originalImgUrl);
      const blob = await response.blob();
      const file = new File([blob], `inner_image_${Date.now()}.jpg`, {
        type: 'image/jpeg',
      });

      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);

      onFileSelect(dataTransfer.files);
    } catch (error) {
      console.error('使用传输图像失败:', error);
      toast.error('Failed to use this image');
    }
  }, [originalImgUrl, onFileSelect]);

  return (
    <section className='section-container'>
      <div
        className={`w-full md:px-16 ${
          originalImgUrl ? 'flex flex-col items-center' : ''
        }`}
      >
        <div
          className={`rounded-2xl bg-[#ffffff] ${
            originalImgUrl ? 'w-fit p-5' : 'p-4'
          }`}
        >
          {uploading ? (
            <UploadingIndicator />
          ) : (
            <div
              className={`flex items-center justify-center rounded-xl ${
                isDragging
                  ? 'bg-blue-200 transition duration-500 ease-in-out'
                  : 'bg-[#ffffff]'
              } ${originalImgUrl ? 'h-fit' : 'h-56 border border-dashed border-neutral-300'} `}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onPaste={onPasteHandler}
            >
              <input
                type='file'
                onChange={handleFileChange}
                className='hidden'
                id='fileInput'
                accept='image/*'
                multiple={maxImageCount > 1}
              />

              <div className='flex flex-col items-center gap-3'>
                {originalImgUrl ? (
                  <ImagePreview
                    imageUrl={originalImgUrl}
                    onNewUpload={handleFileInputClick}
                    onUseImage={handleUseCurrentImage}
                  />
                ) : (
                  <UploadButton
                    MAX_IMAGES={maxImageCount}
                    onClick={handleFileInputClick}
                    multipleUpload={multipleUpload}
                    deviceType={deviceType}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        <TermsAndPolicy router={router} />
      </div>
    </section>
  );
};
